/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2023-present Rakuten Kobo Inc. (https://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External dependencies
import forEach from 'lodash/forEach';

// Internal dependencies
import { preventConcurrentExec, sequential } from '@rakuten/common-util/promise';
import { ERROR_MISSING_ID } from "./push-to-cloud.constants";
import { PushToCloudError } from "./push-to-cloud.error";


// -----------------------------------------------------------------------------
// CONFIGURTATION
// -----------------------------------------------------------------------------

// Tag for log output etc.
const TAG = '[service/push-to-cloud]';

// -----------------------------------------------------------------------------
// SERVICE REGISTRATION
// -----------------------------------------------------------------------------

//service config for service locator
export const config = ["pushToCloud", [
  "fetch",
  "logger",
  "localConfig",
  "netInfo",
  "oAuth",
  "remoteConfig",
  "sessionStorage",
  "queryString"
], createPushToCloudService];

// -----------------------------------------------------------------------------
// PUSH TO CLOUD  SERVICE
// -----------------------------------------------------------------------------

/**
 * factory function
 */
export function createPushToCloudService(
  fetchService,
  logger,
  localConfig,
  netInfo,
  oAuthService,
  remoteConfigTolino,
  sessionStorageService,
  queryStringService
) {

  // ---------------------------------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------------------------------

  // Prevent multiple calls to functions that create the same promise
  const guardedPromise = preventConcurrentExec();


  function errorHandler(errorMessage) {
    logger.error(errorMessage);
  }

  const appConfig = localConfig.getLocalConfig();

  /**
   *
   * A POST API the tolino clients and sample reader can use to add the preview of
   * a specific book,identified by its unique publicationID, to a bookshelf.
   * An important note for this functionality is that all previews pushed to a
   * shelf will have a suffix added to its publicationId and deliverableId fields
   * for the BOSH entity they belong to. This was done, in order to be able to
   * distinguish between the purchased content and the preview sample in the
   * reading-metadata. Additionally, this will also allow us to have a clear
   * differentiation between original content and previews deliverables on family
   * sharing.
   *
   * Example of how publicationId and deliverableId will look after a preview has
   * been pushed to ashelf: PU1111.123456789 _prv
   *
   * @see 4.12.1. bosh_interfaces_core.pdf
   *
   */
  function push2Cloud(publicationId, remoteConfig, tokenInfo) {

    if (tokenInfo.isDemo) {
      return Promise.reject(new PushToCloudError("DEMO_TOKEN"));
    }

    const url = (remoteConfig?.URL_API || appConfig.NETWORK.API_BASE_URL) + `/v1/inventory/${publicationId}/preview`;
    const headers = {
      "accept": "application/json",
      "authorization": `Bearer ${tokenInfo.access_token}`,
      "client-type": remoteConfig.meta.client_type,
      "content-type": "application/json",
      "reseller-id": remoteConfig.meta.reseller_id
    };

    return fetchService.fetch({
      url,
      options: {
        headers,
        method: "POST",
        responseType: "json"
      },
      timeout: 10000
    }).then(response => {
      if (response.ok) {
        return response;
      }

    }).catch(error => errorHandler(error))
  }

  /**
   * Retrieves the currently stored local config
   *
   * @returns {object|null} The currently stored local config
   */
  function pushPublication(publicationId) {
    return guardedPromise(`${TAG}:pushPublication`, () => {
      const processData = sessionStorageService
        .getItem(appConfig.CONSTANTS.PUSH_TO_CLOUD.KEY_AUTH_APP_DATA);

      if (!publicationId) {
        publicationId = processData.publicationId;
      }

      return netInfo.fetchIsConnected()
        .then(online => {

          if (!online) {
            return;
          }

          return sequential([
            remoteConfigTolino.fetchRemoteConfig,
            oAuthService.fetchToken
          ]).then(results => {
            const remoteConfig = results[0];
            const tokenInfo = results[1];

            //TODO remove token and resellerconfig?
            return push2Cloud(publicationId, remoteConfig, tokenInfo);
          }).catch(error => errorHandler(error));
        });

    })
      .catch(error => {
        const errorMessage = error || "GENERAL_ERROR_MESSAGE";
        errorHandler(errorMessage);
        return Promise.reject(errorMessage);
      })
      .finally(() => {
        sessionStorageService
          .removeItem(appConfig.CONSTANTS.PUSH_TO_CLOUD.KEY_AUTH_APP_DATA);
      });
  };

  /**
   * Returns the url used to authenticate before pushing to the cloud
   */
  function getAuthAppUrl(pubId, resellerId = null, locale = "de-DE", theme = "theme-tolino-light") {

    if (!pubId) {
      return Promise.reject(new PushToCloudError(ERROR_MISSING_ID));
    }


    //append oauthsim flag if develop or present in url
    const isOauthSim = Boolean(queryStringService
      .getQueryParameter(appConfig.CONSTANTS.REMOTE_CONFIG.KEY_OAUTH_SIM_ENABLED))
      || appConfig.BUNDLER.BUILD_MODE === appConfig.BUNDLER.BUILD_MODES.DEVELOPMENT;

    //store id to retrieve it after authentication
    sessionStorageService.setItem(appConfig.CONSTANTS.PUSH_TO_CLOUD.KEY_P2C_ID, pubId);

    const authAppUrl = new URL(appConfig.URLS.APP_AUTH);

    const params = {
      app_id: appConfig.APPLICATION.APP_ID,
      locale,
      redirect_uri: window.location.href,
      theme
    };

    if (resellerId) {
      params["reseller_id"] = resellerId;
    }

    if (isOauthSim) {
      params[appConfig.CONSTANTS.REMOTE_CONFIG.KEY_OAUTH_SIM_ENABLED] = "1";
    }

    forEach(params, (value, key) => authAppUrl.searchParams.append(key, value));

    return authAppUrl.toString();
  }

  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  return {
    pushPublication,
    getAuthAppUrl
  };

}
